/**
 * Serbian overrides for the submissions screens.
 *
 * Bosnian moved from ijekavica to ekavica — *uspjela* to *uspela*, *mjesec*
 * to *mesec*, *prijedlog* to *predlog*. Where the two standards agree there
 * is no entry, which is why this file is short and should stay that way.
 *
 * Latin script, matching the rest of the application.
 */

import type { SubmissionsSlice } from '../bs/submissions'

export const submissions: Partial<SubmissionsSlice> = {
  // ---- Shared by more than one screen ------------------------------
  'submissions.status.chain.hint':
    '„{new}” čeka da je neko preuzme, „{inProgress}” da se na njoj radi, „{resolved}” da je posao gotov i čeka potvrdu, a „{closed}” da je prijava završena i da se više ne menja — status menja nadležni, ne onaj ko je prijavio.',

  // ---- The list ----------------------------------------------------
  'submissions.list.empty.description': 'Promenite filtere ili napravite novu prijavu.',
  'submissions.list.next': 'Sledeća',

  // ---- One submission ----------------------------------------------
  'submissions.detail.detailedReport': 'Detaljan izveštaj',
  'submissions.detail.field.resolvedIn': 'Rešeno za',
  'submissions.detail.field.due.hint':
    'Rok se računa sam, prema prioritetu u trenutku prijave — kasnija promena prioriteta ga ne pomera.',
  'submissions.detail.field.resolvedIn.hint':
    'Sati od trenutka kada je prijava poslata do trenutka kada je označena kao rešena — računa se stvarno vreme, a ne radni sati.',

  'submissions.detail.assign.hint':
    'Dodeljivanje nove prijave je istovremeno prebacuje u naredni status.',
  'submissions.detail.assign.who':
    'Prijavu dodeljuju nadležni, komisija i administrator; održavanje je rešava, ali ne može da je prebaci na nekog drugog.',
  'submissions.detail.assign.done': 'Prijava je dodeljena.',
  'submissions.detail.assign.failed': 'Dodela nije uspela.',

  'submissions.detail.status.hint':
    'Ponuđeni su samo statusi u koje prijava sme preći iz trenutnog stanja.',
  'submissions.detail.status.commit':
    'Status menja samo nadležni, svaka promena se upisuje u istoriju sa vašim imenom i vremenom, a onaj ko je prijavio dobije obaveštenje.',
  'submissions.detail.status.note.placeholder': 'Neobavezno — zašto status menjate.',
  'submissions.detail.status.changed': 'Status je promenjen.',
  'submissions.detail.status.failed': 'Promena nije uspela.',
  'submissions.detail.status.locked': 'Prijava je u statusu „{status}” i dalje se ne menja.',

  // All three forms move together: the noun is *ocena* in every one of them.
  'submissions.detail.delete.reviews.one': '{count} ocena',
  'submissions.detail.delete.reviews.few': '{count} ocene',
  'submissions.detail.delete.reviews.many': '{count} ocena',
  'submissions.detail.history.duration':
    'Vreme uz svaku stavku je koliko je prijava stajala u prethodnom statusu, do te promene.',

  'submissions.detail.delete.history': 'cela istorija statusa',
  'submissions.detail.delete.audit':
    'Ko je i kada obrisao ostaje zabeleženo u dnevniku aktivnosti.',
  'submissions.detail.delete.failed': 'Brisanje nije uspelo.',

  // ---- The improvement block ---------------------------------------
  'submissions.improvement.proposal': 'Predlog',
  'submissions.improvement.estimatedSavings': 'Procena uštede',
  'submissions.improvement.estimatedCost': 'Procena troška',

  // ---- The form ----------------------------------------------------
  'submissions.form.type.hint':
    'Kvar, incident i bezbednosni problem idu nadležnom da ih dodeli na rešavanje, a predlog za poboljšanje otvara dodatna polja i ide na ocenu koja može doneti nagradu.',
  // *nalepnica* and *umesto*, as in the scanner strings in sr/tasks.ts.
  'submissions.form.workCenter.hint':
    'Ako mašina ima QR nalepnicu, skenirajte je umesto da tražite centar u spisku — radni centar se tada popuni sam.',
  'submissions.form.media.hint':
    'Fotografija je ono što prijavu čini rešivom — onaj ko dolazi na popravku vidi kvar pre nego krene i zna šta da ponese.',
  'submissions.form.improvement.heading': 'Detalji predloga',
  'submissions.form.improvement.estimate.hint':
    'Ovo je procena — stvarni iznos uštede i troška nadležni upisuje kasnije, kad se predlog sprovede.',
  'submissions.form.improvement.proposal.placeholder': 'Šta konkretno promeniti.',
  'submissions.form.improvement.expectedImpact.placeholder':
    'Ušteda vremena, manje škarta, bezbednije radno mesto…',
  // *sedmica* is the Bosnian week; the example here is the one a Serbian
  // reader would write themselves.
  'submissions.form.improvement.duration.placeholder': 'npr. 2 nedelje',
  'submissions.form.improvement.estimatedSavings': 'Procena uštede ({currency})',
  'submissions.form.improvement.estimatedCost': 'Procena troška ({currency})',
  'submissions.form.voice.hint':
    'Ispričajte šta ne valja umesto da kucate. Poruku šaljemo zajedno sa prijavom.',
  'submissions.form.needContent.toast':
    'Dodajte opis, glasovnu poruku ili prilog pre čuvanja.',
  'submissions.form.savedAfterFailure':
    'Slanje nije uspelo, ali je prijava sačuvana i biće poslata automatski.',
  'submissions.form.saveFailed': 'Čuvanje na uređaju nije uspelo.',
  'submissions.form.sendFailed': 'Slanje nije uspelo.',

  // ---- Tasks -------------------------------------------------------
  'submissions.tasks.empty.manager':
    'Nema zadataka. Dodelite zadatak da bi neko preuzeo rešavanje.',
  'submissions.tasks.help':
    'Kad rešavanje ima više koraka, napravite zadatak za svaki — svaki ima svog izvršioca i svoj rok, i svaki se posebno zatvara. Prijava ostaje otvorena dok je vi ne prebacite dalje, bez obzira na to koliko je zadataka završeno.',
  'submissions.tasks.updateFailed': 'Ažuriranje nije uspelo.',
  'submissions.tasks.assigned': 'Zadatak je dodeljen.',
  'submissions.tasks.form.title.placeholder': 'npr. Zameniti ležaj na hoblu',
  'submissions.tasks.form.description.placeholder':
    'Neobavezno — šta tačno treba uraditi, koji delovi su potrebni.',
  'submissions.tasks.form.submit': 'Dodeli zadatak',

  // ---- The reward scheme -------------------------------------------
  'submissions.reviews.title': 'Ocene',
  'submissions.reviews.add': 'Dodaj ocenu',
  'submissions.reviews.empty.manager':
    'Još nema ocena. Nadležni daje prvu ocenu, komisija donosi konačnu odluku i nagradu.',
  'submissions.reviews.empty': 'Prijava još nije ocenjena.',
  'submissions.reviews.points.hint':
    'Bodovi se dodeljuju samo predlozima za poboljšanje, po tri kriterijuma, svaki 0–5.',
  'submissions.reviews.awardField.hint': 'Samo komisija može dodeliti novčanu nagradu.',
  'submissions.reviews.comment.placeholder': 'Zašto je predlog prihvaćen ili odbijen.',
  'submissions.reviews.save': 'Sačuvaj ocenu',
  'submissions.reviews.saved': 'Ocena je sačuvana.',

  // ---- Spoken messages ---------------------------------------------
  'submissions.voice.empty': 'Snimak je prazan. Proverite mikrofon i pokušajte ponovo.',

  // ---- Who was working the centre ----------------------------------
  'submissions.workers.hint':
    'Ko je radio na ovom centru u periodu koji ocenjujete. Ocenjuje se radni centar, ne radnik — ovo je podatak o tome ko ga je održavao.',
  'submissions.workers.noResults.description': 'Promenite pretragu.',
  'submissions.edit.title': 'Izmena prijave',
  'submissions.edit.saved': 'Prijava je izmenjena.',
  'submissions.edit.action': 'Izmeni',
  'submissions.edit.closed':
    'Ova prijava više ne može da se menja — neko ju je već preuzeo ili je zatvorena. Dodajte komentar ili zamolite odgovorno lice.',
  'submissions.edit.scoredLocked':
    'Komisija je već ocenila ovaj predlog, pa se njegov sadržaj više ne menja. Ocena je data upravo na ovaj tekst.',
  'submissions.edit.files.hint':
    'Prilozi se menjaju odmah — dugme „Odustani" ispod ne vraća obrisani fajl.',
  'submissions.realisation.hint':
    'Šta je predlog stvarno doneo, kada je sproveden i ko ga je sproveo. Popunjava se posle realizacije — i posle zatvaranja prijave.',
  'submissions.realisation.against': 'Procenjeno: {amount}',
  'submissions.realisation.dateHint': 'Ne može da bude u budućnosti.',

  // Ekavica for the strings the Bosnian copy carries a jat into.
  'submissions.insights.lifecycle.title': 'Gde je vreme otišlo',
  'submissions.insights.benchmark.sample': 'Poređeno sa {count} rešenih prijava.',
  'submissions.insights.scoring.title': 'Kako je ocenjeno',
  'submissions.insights.scoring.hint': 'Iz čega se sastoji ocena komisije.',
  'submissions.insights.scoring.median': 'prosek prihvaćenih predloga: {median}',
  'submissions.insights.realisation.hint': 'Procena iz predloga naspram onoga što se stvarno desilo.',
  'submissions.insights.realisation.estimated': 'Procena',
  'submissions.insights.recurrence.hint':
    'Isti radni centar ima još {count} ovakvih prijava u poslednjih {days} dana.',

  // ---- The list as queues ------------------------------------------
  // *ovde* for *ovdje* runs through every empty-list sentence.
  'submissions.tab.rijesene': 'Rešene',
  'submissions.tab.dodijeljene-meni': 'Dodeljene meni',
  'submissions.tabs.hint':
    '„U radu” su sve otvorene prijave koje je neko preuzeo. „Rešene” čekaju da ih nadležni potvrdi i zatvori.',
  'submissions.period.month': 'Ovaj mesec',
  'submissions.period.lastMonth': 'Prošli mesec',
  'submissions.empty.title': 'Ovde trenutno nema prijava',
  'submissions.empty.otvorene':
    'Ovde stoje prijave na kojima se još radi. Novu prijavu pravite dugmetom „+ Novo” gore desno.',
  'submissions.empty.nove': 'Ovde stižu nove prijave dok ih niko ne preuzme.',
  'submissions.empty.u-radu': 'Ovde su prijave koje je neko preuzeo, a još nisu rešene.',
  'submissions.empty.van-roka': 'Ovde se pojavljuju otvorene prijave kojima je prošao rok. Sada nijedna nije kasna.',
  'submissions.empty.rijesene': 'Ovde čekaju rešene prijave dok ih nadležni ne potvrdi i zatvori.',
  'submissions.empty.dodijeljene-meni': 'Ovde se pojavljuju otvorene prijave koje su dodeljene vama.',
  'submissions.empty.moje': 'Ovde su sve prijave koje ste vi poslali. Novu pravite dugmetom „+ Novo” gore desno.',
  'submissions.empty.sve': 'Ovde su sve prijave iz vaših pogona, i otvorene i zatvorene.',

  // ---- One report --------------------------------------------------
  'submissions.next.title': 'Sledeći korak',
  'submissions.next.reason.close.placeholder': 'npr. Ležaj zamenjen, mašina radi normalno.',
  'submissions.next.assigned': 'Označi kao dodeljenu',
  'submissions.next.resolve': 'Označi rešeno',
  'submissions.history.assigned': 'Dodeljeno: {name}',
  'submissions.history.taskVerified': 'Zadatak proveren: „{title}”',

  // ---- The new-report form -----------------------------------------
  'submissions.form.machine.change': 'Promeni',
  'submissions.form.machine.scanTitle': 'Skenirajte QR nalepnicu na mašini',
}
